import useFormHook from "@shared/hooks/useFormHook";
import LoadingButton from "@/shared/components/LoadingButton";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "@/shared/components/CustomDatePicker";
import { identificatorsService } from "@/modules/additionalSettings/nomenclatures/services/identificators";
import { lengthOfServiceService } from "../services/lengthOfServiceService";
import { extractErrorMessage } from "@/shared/services/blockingError";
import { notifySuccess } from "@/config/tostifyConfig/tostifyConfig";

const CAT_WORK_DETER_ID = 8;
const FORM_ID = 'newTableForm';

const formatDateForApi = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string') {
        return dateValue.split('T')[0];
    }
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function NewTableForm({ onClose, type = 'create', onSuccess }) {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoryOptionsLoading, setCategoryOptionsLoading] = useState(false);
    const [tblClassId, setTblClassId] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [selectedCatWorkId, setSelectedCatWorkId] = useState('');
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        const fetchCategoryOptions = async () => {
            setCategoryOptionsLoading(true);
            try {
                const data = await identificatorsService.getNomenclatures(CAT_WORK_DETER_ID);
                setCategoryOptions(
                    (Array.isArray(data) ? data : [])
                        .filter((item) => item.active !== false)
                        .map((item) => ({
                            id: item.id,
                            label: `${item.note} - ${item.value}`,
                        }))
                );
            } catch (fetchError) {
                console.error('Error fetching category of work options:', fetchError);
                setCategoryOptions([]);
            } finally {
                setCategoryOptionsLoading(false);
            }
        };

        fetchCategoryOptions();
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (type !== 'create') {
            return;
        }

        setError(null);

        if (!tblClassId || !fromDate || !selectedCatWorkId) {
            setError(t('common.required'));
            return;
        }

        setLoading(true);
        try {
            await lengthOfServiceService.createTblClass({
                tblClassId: Number(tblClassId),
                fromDate: formatDateForApi(fromDate),
                catWorkId: Number(selectedCatWorkId),
                active: isActive,
            });
            notifySuccess(t('common.createdSuccessfully'));
            onSuccess?.();
            onClose();
        } catch (submitError) {
            console.error('Error creating tbl-class:', submitError);
            setError(extractErrorMessage(submitError));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {t('Compensations as per labor contracts.lengthOfService.firstFormTitle')}
                        </h4>
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        <form id={FORM_ID} className="region-form" onSubmit={handleSubmit}>
                            <div className="flex-column">
                                <div className="form-section width-90">
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t("Compensations as per labor contracts.lengthOfService.table.headers.tableNumber")}
                                            <span className="required">*</span>
                                        </span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={tblClassId}
                                            onChange={(e) => setTblClassId(e.target.value)}
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t("Compensations as per labor contracts.lengthOfService.table.headers.fromDate")}
                                            <span className="required">*</span>
                                        </span>
                                        <CustomDatePicker
                                            name="fromDate"
                                            value={fromDate}
                                            onChange={setFromDate}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">
                                            {t("Compensations as per labor contracts.lengthOfService.table.headers.categoryOfWork")}
                                            <span className="required">*</span>
                                        </span>
                                        <select
                                            className="form-control"
                                            value={selectedCatWorkId}
                                            onChange={(e) => setSelectedCatWorkId(e.target.value)}
                                            disabled={categoryOptionsLoading}
                                        >
                                            <option value="">
                                                {categoryOptionsLoading ? t('common.loading') : t('common.select')}
                                            </option>
                                            {categoryOptions.map((option) => (
                                                <option key={option.id} value={option.id}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t("Compensations as per labor contracts.lengthOfService.table.headers.additionalClassTableTitle")}</span>
                                        <select className="form-control" disabled>
                                            <option value="1">{t("common.yes")}</option>
                                            <option value="2">{t("common.no")}</option>
                                        </select>
                                    </div>
                                    <div className="flex-row">
                                        <span className="span-bold">{t("Compensations as per labor contracts.lengthOfService.table.headers.active")}</span>
                                        <div className="input-checkbox-wrapper">
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={(e) => setIsActive(e.target.checked)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <LoadingButton
                            type="submit"
                            form={FORM_ID}
                            className="btn btn-primary"
                            disabled={loading || type !== 'create'}
                        >
                            {loading ? t('common.saving') : t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
